import React, { PropsWithChildren, useEffect } from 'react';
import { DashboardHeader } from './DashboardHeader';
import './DashboardLayout.scss';
import { PermissionManager } from '../../features/permission-manager/PermissionManager';
import { Tabs } from '../../features/tabs/Tabs';
import { ERouterPath } from '../../features/router/ERouterPath';
import classNames from 'classnames';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { ISessionAppState } from '../../app/session/reducer';
import { UserAccountRoleEnum } from '../../api/types';
import { Icon } from '../../features/icon/Icon';
import { InactiveScreen } from '../../features/inactive-screen/InactiveScreen';

interface IDashboardLayoutProps {
  skipScrollToTop?: boolean;
  limitSizeToView?: boolean;
  className?: string;
  size?: 'big'|'small';
  hasBackGround?: boolean;
  noTabs?: boolean;
  /**
   * Set to false to disable back button
   * Set to true to show back button that functions as browser back button
   * Set to `ERouterPath|string` to direct to user to another route
   * Set to `undefined` to let the back button navigate to `ERouterPath.personalDossiers` if user is caregiver or admin (default)
   */
  backButton?: boolean|ERouterPath|string;
  showTabsInitially?: boolean;
  fullScreenMobile?: boolean;
  grayBackgroundMobile?: boolean;
}

export function DashboardLayout({ skipScrollToTop, children, limitSizeToView, className, size, hasBackGround, noTabs, backButton, showTabsInitially, fullScreenMobile, grayBackgroundMobile }: PropsWithChildren<IDashboardLayoutProps>): JSX.Element {
  const { user } = useSelector<ApplicationState, ISessionAppState>(state => state.session);
  const history = useHistory();

  useEffect(() => {
    if (!skipScrollToTop) {
      window.scrollTo(0, 0);
    }
  }, [skipScrollToTop]);

  const handleBackClick = (): void => {
    if (backButton && typeof backButton === 'string') {
      history.push(backButton);
    } else if (backButton === undefined && user) {
      switch (user.accountRole) {
        case UserAccountRoleEnum.caregiver:
        case UserAccountRoleEnum.adminCaregiver:
          history.push(ERouterPath.personalDossiers);
          break;
        case UserAccountRoleEnum.administration:
          history.push(ERouterPath.allDossiers);
          break;
        default:
          history.push(ERouterPath.selectFile);
      }
    } else {
      history.goBack();
    }
  };

  const classNameMainContainer = classNames(
    'dashboard-layout__main-content',
    {
      [`dashboard-layout__main-content--${size ?? ''}`]: size,
      'dashboard-layout__main-content--background': hasBackGround,
    }
  );

  let displayBackButton = backButton !== false;
  if (backButton === undefined && user && [UserAccountRoleEnum.adminCaregiver, UserAccountRoleEnum.administration, UserAccountRoleEnum.caregiver].includes(user.accountRole) === false) {
    displayBackButton = false;
  }

  const classNameDashboardLayout = classNames(
    'dashboard-layout',
    {
      'dashboard-layout--limit-to-view': limitSizeToView,
      'dashboard-layout--fullscreen-mobile': fullScreenMobile,
    }
  );

  return (
    <div className={classNameDashboardLayout}>
      <InactiveScreen />
      <PermissionManager />
      <DashboardHeader className="dashboard-layout__header" />
      <div className={classNames('dashboard-layout__main-content-container', className)}>
        {
          displayBackButton &&
           <div className="dashboard-layout__back">
             <button className="dashboard-layout__back-button" onClick={handleBackClick}><Icon className="dashboard-layout__back-button-icon" icon="back"/>Terug</button>
           </div>
        }
        <div className={classNameMainContainer}>
          {
            noTabs ? children : <Tabs fullScreenMobile={fullScreenMobile} showTabsInitially={showTabsInitially} grayBackgroundMobile={grayBackgroundMobile}>{ children }</Tabs>
          }
        </div>
      </div>
    </div>
  );
}
